import { COLLECTIONS, FEATURED_TIMELINE, CATEGORY_META } from "@/mocks/gossip";
import SectionHeading from "./SectionHeading";

function CollectionCard({ item }: { item: (typeof COLLECTIONS)[number] }) {
  const meta = CATEGORY_META[item.category];
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-transform duration-300 hover:-translate-y-1">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={item.cover}
          alt={`${item.name} 合集封面`}
          title={`${item.name} 完整事件合集`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-foreground-950/70 px-3 py-1 text-xs font-bold text-background-50 backdrop-blur">
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
          {meta.name}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950">{item.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1 font-medium text-secondary-800">
            <i className="ri-node-tree" />
            {item.stage} 个节点
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1 font-medium text-secondary-800">
            <i className="ri-time-line" />
            跨度 {item.span}
          </span>
          <span className="ml-auto whitespace-nowrap">{item.members}</span>
        </div>
      </div>
    </article>
  );
}

export default function Collections() {
  const ft = FEATURED_TIMELINE;
  const meta = CATEGORY_META[ft.category];
  return (
    <section id="collections" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="整瓜合集 · 时间线"
            title="事件已按顺序排好，一口气读完"
            desc="明星瓜与行业瓜都不再东翻西帖。每个合集都把完整事件按时间节点排成时间线，新老瓜友都能在清晰的路径里找回每一段线索。"
          />
          <a
            href="#contact"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-6 py-2.5 text-sm font-bold text-primary-600 transition-colors hover:border-primary-200 hover:bg-primary-100"
          >
            <i className="ri-notification-3-line" />
            订阅更新提醒
          </a>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 overflow-hidden rounded-2xl border border-background-200 bg-background-50 lg:grid-cols-[5fr_6fr]">
          <div className="flex flex-col justify-between gap-6 p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
                <i className="ri-fire-fill" />
                {ft.badge}
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold text-secondary-800">
                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                {meta.name}
              </span>
            </div>
            <div>
              <h3 className="font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
                {ft.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{ft.lead}</p>
            </div>
            <blockquote className="border-l-4 border-primary-500 pl-4 text-sm leading-relaxed text-primary-700">
              {ft.conclusion}
            </blockquote>
          </div>

          <div className="border-t border-background-200 p-6 md:p-8 lg:border-l lg:border-t-0">
            <ol className="relative space-y-6 border-l border-secondary-200 pl-6">
              {ft.stages.map((s, idx) => (
                <li key={s.title} className="relative">
                  <span
                    className={`absolute -left-[31px] flex h-3.5 w-3.5 items-center justify-center rounded-full ring-4 ring-background-50 ${
                      idx === ft.stages.length - 1 ? "bg-accent-500" : "bg-secondary-500"
                    }`}
                  />
                  <p className="text-xs font-bold uppercase tracking-wide text-foreground-400">{s.time}</p>
                  <h4 className="mt-1 font-heading text-base font-bold text-foreground-950">{s.title}</h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {COLLECTIONS.map((c) => (
            <CollectionCard key={c.id} item={c} />
          ))}
        </div>
      </div>
    </section>
  );
}